import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { WS_URL, VTE_ENABLE_SOCKET } from '../config/constants';
import { STORAGE_KEYS } from '@shared/constants';
import { useAuth } from '../contexts/AuthContext';

interface AttendanceUpdateData {
  id: number;
  userId: number;
  type: 'CHECK_IN' | 'CHECK_OUT';
  status: string;
  method: string;
  date: string | Date;
  time: string;
  latitude: number;
  longitude: number;
  distance?: number;
  note?: string | null;
  user?: {
    id: number;
    fullName: string;
    employeeCode: string;
    email: string;
  } | null;
  gpsLocation?: {
    id: number;
    name: string;
    address: string;
  } | null;
  workShift?: {
    id: number;
    name: string;
    code: string;
  } | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

interface UseAttendanceSocketOptions {
  onCheckIn?: (data: AttendanceUpdateData) => void;
  onCheckOut?: (data: AttendanceUpdateData) => void;
  onUpdate?: (data: AttendanceUpdateData) => void;
}

export const useAttendanceSocket = (options: UseAttendanceSocketOptions = {}) => {
  const { isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const isMountedRef = useRef(true);
  // Lưu callbacks trong ref để tránh tạo lại socket khi callbacks thay đổi
  const callbacksRef = useRef(options);

  // Cập nhật callbacks ref mỗi khi options thay đổi (không trigger re-render)
  useEffect(() => {
    callbacksRef.current = options;
  }, [options.onCheckIn, options.onCheckOut, options.onUpdate]);

  useEffect(() => {
    isMountedRef.current = true;

    // Chỉ kết nối nếu socket được enable và user đã đăng nhập
    if (!VTE_ENABLE_SOCKET) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    if (!isAuthenticated) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    const initSocket = async () => {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (!token) {
          return;
        }

        // Kết nối với attendance namespace
        // Socket.IO tự động convert http:// -> ws:// và https:// -> wss://
        // Loại bỏ /api nếu có vì Socket.IO namespace không dùng /api prefix
        let baseUrl = WS_URL;
        
        // Nếu WS_URL là HTTP/HTTPS, giữ nguyên (Socket.IO sẽ tự convert)
        // Nếu đã là ws:// hoặc wss://, giữ nguyên
        if (baseUrl.endsWith('/api')) {
          baseUrl = baseUrl.replace(/\/api$/, '');
        }
        // Loại bỏ trailing slash
        baseUrl = baseUrl.replace(/\/+$/, '');
        
        // Socket.IO namespace format: baseUrl/namespace
        const attendanceNamespace = `${baseUrl}/attendance`;
        
        const newSocket = io(attendanceNamespace, {
          auth: { token },
          transports: ['websocket'],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 2000,
          timeout: 5000,
          autoConnect: true,
        });

        newSocket.on('connect', () => {
          if (isMountedRef.current) {
            setIsConnected(true);
          }
          // Subscribe để nhận attendance updates
          newSocket.emit('attendance:subscribe');
        });

        newSocket.on('disconnect', () => {
          if (isMountedRef.current) {
            setIsConnected(false);
          }
        });

        newSocket.on('connect_error', () => {
          if (isMountedRef.current) {
            setIsConnected(false);
          }
        });

        // Lắng nghe check-in event - sử dụng callbacksRef để luôn có callback mới nhất
        newSocket.on('attendance:check-in', (data: AttendanceUpdateData) => {
          if (callbacksRef.current.onCheckIn) {
            callbacksRef.current.onCheckIn(data);
          }
        });

        // Lắng nghe check-out event
        newSocket.on('attendance:check-out', (data: AttendanceUpdateData) => {
          if (callbacksRef.current.onCheckOut) {
            callbacksRef.current.onCheckOut(data);
          }
        });

        // Lắng nghe update event (tổng quát)
        newSocket.on('attendance:update', (data: AttendanceUpdateData) => {
          if (callbacksRef.current.onUpdate) {
            callbacksRef.current.onUpdate(data);
          }
        });

        socketRef.current = newSocket;
      } catch (error) {
        if (isMountedRef.current) {
          setIsConnected(false);
        }
      }
    };

    initSocket();

    return () => {
      isMountedRef.current = false;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated]); // Chỉ phụ thuộc vào isAuthenticated, không phụ thuộc vào callbacks

  return {
    isConnected,
    socket: socketRef.current,
  };
};
